package ai.wagemix.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import ai.wagemix.web.theme.WMCarouselTransitionVar
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.onFocusIn
import com.varabyte.kobweb.compose.ui.modifiers.onFocusOut
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.modifiers.transform
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.awaitAnimationFrame
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div

/** One fact of a [WMFactCarousel]: a large figure over its label ("24+ million" / "data points"). */
interface WMFact {
    val value: String
    val label: String

    companion object
}

fun WMFact(value: String, label: String): WMFact = WMFactData(value, label)

private data class WMFactData(override val value: String, override val label: String): WMFact

private const val REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

/** The centred slide is half the carousel's width, so each neighbour shows half of itself. */
private const val SLIDE_WIDTH_PERCENT = 50
private const val SIDE_SLIDE_OPACITY = 0.4
private const val SIDE_SLIDE_SCALE = 0.75

val WMFactCarouselStyle = CssStyle.base { Modifier.fillMaxWidth().overflow(Overflow.Hidden) }

val WMFactCarouselTrackStyle = CssStyle.base { Modifier.fillMaxWidth().display(DisplayStyle.Flex) }

val WMFactCarouselSlideStyle = CssStyle.base {
    Modifier
        .width(SLIDE_WIDTH_PERCENT.percent)
        .flexShrink(0)
}

/**
 * Penpot `Landing` trust strip: [facts] rotate through the centre, one every [dwellMillis], with the previous and next
 * fact showing at half their width on either side, dimmed and at three quarters of their size. Moving into or out of
 * the centre slides, fades and scales together over `WMCarouselTransitionVar`. The loop is seamless (the facts are laid out three times and the
 * track jumps back by one set, unanimated, once the last copy is centred). Rotation pauses while the pointer is over
 * the carousel or focus is inside it; with `prefers-reduced-motion` the facts change without any animation.
 */
@Composable
fun WMFactCarousel(
    facts: List<WMFact>,
    accessibilityString: String,
    slideLabel: (position: Int, count: Int) -> String,
    modifier: Modifier = Modifier,
    dwellMillis: Long = 4_000L
) {
    val count = facts.size
    if (count == 0) {
        return
    }
    val reducedMotion = remember { window.matchMedia(REDUCED_MOTION_QUERY).matches }
    // The centred slide, an index into the three laid-out copies; it stays within the middle copy between ticks.
    var centred by remember(count) { mutableStateOf(count) }
    var sliding by remember { mutableStateOf(false) }
    var hovered by remember { mutableStateOf(false) }
    var focused by remember { mutableStateOf(false) }
    val paused = hovered || focused

    LaunchedEffect(count, paused, dwellMillis) {
        if (paused) {
            return@LaunchedEffect
        }
        while (true) {
            delay(dwellMillis)
            if (centred >= 2 * count) {
                // Jump back to the same fact in the middle copy without a transition, then let a frame commit it.
                sliding = false
                centred -= count
                window.awaitAnimationFrame()
                window.awaitAnimationFrame()
            }
            sliding = !reducedMotion
            centred += 1
        }
    }

    Box(
        modifier = WMFactCarouselStyle.toModifier()
            .role("region")
            .ariaLabel(accessibilityString)
            .attrsModifier { attr("aria-roledescription", "carousel") }
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .onFocusIn { focused = true }
            .onFocusOut { focused = false }
            .then(modifier)
    ) {
        Div(
            WMFactCarouselTrackStyle.toModifier()
                .attrsModifier { attr("aria-live", if (paused) "polite" else "off") }
                .transform { translateX((SLIDE_WIDTH_PERCENT / 2 - centred * SLIDE_WIDTH_PERCENT).percent) }
                .thenIf(sliding) { Modifier.transition(Transition.of("transform", WMCarouselTransitionVar.value())) }
                .thenIf(!sliding) { Modifier.transition(Transition.None) }
                .toAttrs()
        ) {
            repeat(3 * count) { slot ->
                val fact = facts[slot % count]
                val isCentred = slot == centred
                Box(
                    modifier = WMFactCarouselSlideStyle.toModifier()
                        .role("group")
                        .ariaLabel(slideLabel(slot % count + 1, count))
                        .attrsModifier { attr("aria-roledescription", "slide") }
                        .ariaHidden(!isCentred)
                        .thenIf(!isCentred) { Modifier.opacity(SIDE_SLIDE_OPACITY).scale(SIDE_SLIDE_SCALE) }
                        // The unanimated jump back to the middle copy must not fade or grow the newly centred copy.
                        .thenIf(sliding) {
                            Modifier.transition(
                                Transition.of("opacity", WMCarouselTransitionVar.value()),
                                Transition.of("scale", WMCarouselTransitionVar.value())
                            )
                        }
                        .thenIf(!sliding) { Modifier.transition(Transition.None) },
                    contentAlignment = Alignment.Center
                ) {
                    WMFigure(fact.value, fact.label)
                }
            }
        }
    }
}
